import { objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import consent from '@buzzfeed/consent';
import EventManager from '../../../../core/event-manager/index.js';
import ads from '../../../../core/ads.js';
import { setLanguage } from '../../../../services/localization/index.js';
import service from '../../../../services/consent/index.js';
import { AdsContext } from '../../contexts/index.js';

/**
 * Dependencies have to be singletons as they can be injected into ads core only once
 * and there's currently no way to re-inject them on navigation,
 * so we're making use of objects being mutatable instead.
 * @todo Rework this?
 */

var dependencies = {
  /** will not be overridden by consuming services */
  eventBus: new EventManager(),

  /** must be overridden by consuming services */
  env: {},

  /** may be overridden by consuming services, if they support non-English editions */
  localization: {
    edition: 'en-us',
    language: 'en',
    country: 'us'
  },

  /** may be overridden by consuming services, if they have an ABeagle client */
  abeagle: {
    getExperimentVariant: function getExperimentVariant() {
      return Promise.resolve('control');
    },
    isOn: function isOn() {
      return Promise.resolve(false);
    }
  },

  /** may be overridden by consuming services */
  tracking: {
    track: function track() {},
    trackPrebidEvents: function trackPrebidEvents() {}
  },

  /** is not supposed to be overridden by consuming services, but may be if needed */
  // copy so that the exported object is not mutated with `Object.assign`
  gdpr: _objectSpread2({}, service),

  /** is not supposed to be overridden by consuming services, but may be if needed */
  // @todo Use the consent module for CCPA too
  ccpa: {
    getConsentValue: function getConsentValue() {
      return consent.framework.needsConsent() ? consent.rules.fetchCCPAValue() : Promise.resolve(null);
    }
  }
};
/**
 * @todo
 */

function AdsProvider(_ref) {
  var pageId = _ref.pageId,
      getPageContext = _ref.getPageContext,
      _ref$pageLanguage = _ref.pageLanguage,
      pageLanguage = _ref$pageLanguage === void 0 ? 'en' : _ref$pageLanguage,
      _ref$adsEnabled = _ref.adsEnabled,
      adsEnabled = _ref$adsEnabled === void 0 ? true : _ref$adsEnabled,
      getPageTargeting = _ref.getPageTargeting,
      getSlotTargeting = _ref.getSlotTargeting,
      children = _ref.children;

  // @todo Make an enum listing all possible context statuses
  var _useState = useState({
    status: 'loading'
  }),
      _useState2 = _slicedToArray(_useState, 2),
      context = _useState2[0],
      setContext = _useState2[1];

  var adsInitialized = useRef(false);
  var prevPageId = useRef(); // This is essentially `getDerivedStateFromProps`, but in a function component
  // (see https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops).
  // It will stop the current rendering cycle (where page id and ads context are out of sync)
  // and start a new cycle immediately (where page id and ads context match).
  // This way context consumers down the component tree
  // can rely on the ads context status alone, without the need to also check page id

  if (!prevPageId.current || pageId !== prevPageId.current) {
    if (adsEnabled && context.status !== 'loading') {
      setContext({
        status: 'loading'
      });
    } else if (!adsEnabled && context.status !== 'disabled') {
      setContext({
        status: 'disabled'
      });
    }

    setLanguage(pageLanguage);
  }

  useEffect(function () {
    if (pageId === prevPageId.current) {
      return;
    }

    prevPageId.current = pageId;

    if (!adsEnabled) {
      return;
    }

    Promise.resolve(getPageContext()).then(function (deps) {
      // mutating the dependencies on navigation, see the note at the top of the module
      Object.keys(dependencies).forEach(function (name) {
        if (name in deps && name !== 'eventBus') {
          // assuming the objects don't have getters/setters, only plain properties and/or methods
          Object.assign(dependencies[name], deps[name]);
        }
      });
      ads.configure({
        customTargetingPage: getPageTargeting,
        customTargetingSlot: getSlotTargeting
      }); // if adlib core was previously initialized, then we should reset

      if (adsInitialized.current) {
        ads.reset();
      } else {
        ads.inject(dependencies); // @todo Catch?

        ads.start().then(function () {
          adsInitialized.current = true;
        });
      }

      setContext(_objectSpread2(_objectSpread2(_objectSpread2({}, dependencies), deps), {}, {
        pageId: pageId,
        status: 'loaded'
      }));
    });
  }, [pageId, adsEnabled, getPageContext, getPageTargeting, getSlotTargeting]);
  return /*#__PURE__*/React.createElement(AdsContext.Provider, {
    value: context
  }, children);
}
AdsProvider.propTypes = {
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  getPageContext: PropTypes.func.isRequired,
  pageLanguage: PropTypes.string,
  adsEnabled: PropTypes.bool,
  getPageTargeting: PropTypes.func,
  getSlotTargeting: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

export { AdsProvider as default };
